import { capitalize, isEmpty, isFunction, isUndefined } from "lodash";
import React, { useMemo } from "react";
import { AssetEsDto, HostProperties } from "types/assetdetails";
import { formatArrayField } from "utils/assetUtils";
import { CellRenderers } from "utils/tableUtils";
import { CopyContent } from "views/Findings/FindingDetails/FindingDetailsSourceInfo";

import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";

export const useAssetHostFields = (assetDetails: AssetEsDto) => {
  const hostFields = useMemo(() => {
    const hostDetails = {
      ...assetDetails.hostProperties,
      name: assetDetails?.name,
    };

    const formatHostSubType = (hostSubType: string): string => {
      if (!hostSubType) return "";
      if (["KUBERNETES_NODE"].includes(hostSubType)) {
        return hostSubType
          .split("_")
          .map((word) => capitalize(word))
          .join(" ");
      }
      return hostSubType;
    };

    const subSectionFields =
      SUB_SECTION_MAP[hostDetails?.hostSubType as keyof typeof SUB_SECTION_MAP];
    const hostSubType = formatHostSubType(hostDetails?.hostSubType?.[0]);

    const fields = [
      {
        label: "DNS Name",
        name: "dns",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(assetDetails.dnsName),
        valueClassName: "break-content",
        extra: !isEmpty(assetDetails.dnsName) ? (
          <CopyContent content={assetDetails.dnsName} fieldLabel="DNS Name" />
        ) : null,
      },
      {
        label: "Cloud Provider",
        name: "cloudProvider",
        formatter: () => formatArrayField(assetDetails.cloudProvider),
      },
      {
        label: "Region",
        name: "region",
        containerClassName: "flex-column",
        formatter: () => {
          // For HOST type, read from hostProperties.region first
          // Fall back to cloudResourceProperties.region or location
          const { cloudResourceProperties: cloudDetails, location } =
            assetDetails;
          if (hostDetails?.region) {
            return formatArrayField(hostDetails.region);
          }
          if (cloudDetails?.region) {
            return formatArrayField(cloudDetails.region);
          }
          return formatArrayField(location);
        },
      },
      {
        label: "OS",
        name: "os",
        formatter: () =>
          Array.isArray(assetDetails.os) && assetDetails.os.length > 0 ? (
            <ACResponsiveTags
              tagProps={{ mode: "label" }}
              tags={assetDetails.os}
            />
          ) : (
            CellRenderers.VALUE_OR_NA()
          ),
      },
      {
        label: "OS Version",
        name: "osVersion",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(assetDetails.osVersion),
      },
      {
        label: "Architecture",
        name: "architecture",
        containerClassName: "flex-column",
        formatter: () => {
          const cloudDetails = assetDetails?.cloudResourceProperties;
          return formatArrayField(
            cloudDetails?.architecture || hostDetails?.architecture,
          );
        },
      },
      {
        label: "IPV4",
        name: "ipv4",
        formatter: () => formatArrayField(assetDetails.ipv4),
        extra: !isEmpty(assetDetails.ipv4) ? (
          <CopyContent content={assetDetails.ipv4} fieldLabel="IPV4" />
        ) : null,
      },
      {
        label: "IPV6",
        name: "ipv6",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(assetDetails.ipv6),
        extra: !isEmpty(assetDetails.ipv6) ? (
          <CopyContent content={assetDetails.ipv6} fieldLabel="IPV6" />
        ) : null,
      },
      {
        label: "Account ID",
        name: "cloudAccountId",
        formatter: () => formatArrayField(assetDetails.cloudAccountId),
      },
      {
        label: "ARN",
        name: "arn",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(assetDetails.arn),
        extra: !isEmpty(assetDetails.arn) ? (
          <CopyContent content={assetDetails.arn.join(", ")} fieldLabel="ARN" />
        ) : null,
      },
      ...(hostDetails?.hostSubType && !isUndefined(subSectionFields)
        ? [
            {
              label: `${hostSubType} Details`,
              name: "header1",
            },
            ...(isFunction(subSectionFields)
              ? subSectionFields(hostDetails)
              : []),
          ]
        : []),
    ];
    return fields.map((hostField) => ({ ...hostField, disabledPin: true }));
  }, [assetDetails]);

  return hostFields;
};

const getKubernetesNodeDetails = (hostDetails: HostProperties) => [
  {
    label: "Kubernetes Flavour",
    name: "kubernetesFlavour",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(hostDetails.kubernetesFlavor),
  },
  {
    label: "Kubernetes Version",
    name: "kubernetesVersion",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(hostDetails.kubernetesVersion),
  },
  {
    label: "Authentication Mode",
    name: "authenticationMode",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(hostDetails.authenticationMode),
  },
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(hostDetails.nativeTypes),
  },
  {
    label: "Kubernetes Cluster Name",
    name: "kubernetesClusterName",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(hostDetails.kubernetesClusterName),
  },
];

const SUB_SECTION_MAP = {
  KUBERNETES_NODE: getKubernetesNodeDetails,
};
